import type { Being } from '../models/Being';
import { LEVEL1 } from '../models/constants';
import { DeadState } from '../controllers/states/being/DeadState';

const SOCIAL_COHESION = LEVEL1.SOCIAL_COHESION;

/**
 * Encapsulates the social-cohesion metric: tracks the running score,
 * advances it each tick based on how clustered the beings are, and exposes
 * read-only accessors for UI and win-condition checks.
 *
 * Rules (unchanged from the original Level1 implementation):
 * - Each living being that has at least one neighbour within
 *   `SOCIAL_COHESION.NEARBY_RADIUS_SQ` grid units adds that neighbour count
 *   to the score (capped at `SOCIAL_COHESION.TARGET`).
 * - Each living being with no neighbours has a `SOCIAL_COHESION.DECAY_PROBABILITY`
 *   chance of subtracting 1 from the score (floored at 0).
 */
export class SocialCohesionService {
    private _score = 0;

    /** Current cohesion score, clamped to [0, TARGET]. */
    public get score(): number {
        return this._score;
    }

    /** The target score required to complete the level. */
    public get target(): number {
        return SOCIAL_COHESION.TARGET;
    }

    /** `true` when the cohesion score has reached the target. */
    public get isComplete(): boolean {
        return this._score >= SOCIAL_COHESION.TARGET;
    }

    /**
     * Advances the cohesion score by one simulation tick.
     *
     * @param beings - All beings currently in the world.
     */
    public tick(beings: Being[]): void {
        for (const being of beings) {
            if (being.state === DeadState.stateName) continue;

            const nearbyCount = beings.filter(other => {
                if (other === being) return false;
                const dx = other.x - being.x;
                const dz = other.z - being.z;
                return dx * dx + dz * dz < SOCIAL_COHESION.NEARBY_RADIUS_SQ;
            }).length;

            if (nearbyCount > 0) {
                this._score = Math.min(SOCIAL_COHESION.TARGET, this._score + nearbyCount);
            } else if (Math.random() < SOCIAL_COHESION.DECAY_PROBABILITY) {
                this._score = Math.max(0, this._score - 1);
            }
        }
    }
}
